import React, { useState, useMemo, useEffect } from "react";
import { format, parseISO } from "date-fns";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
} from "@mui/material";
import { RotateCcw, Copy, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useZustandStore from "../../zustandStore/useZustandStore";
import usePermissions from "../../hooks/usePermissions";
import {
  AGENCY_LIST_PERMISSIONS,
  PERMISSION_CODES,
} from "../../utils/constants/permissions";
import AdsAPIs from "../../apiRoutes/Ads";
import AgencyAPIs from "../../apiRoutes/Agency";
import {
  GET_AD_JOURNEY_QUERY_KEY,
  GET_AGENCIES_QUERY_KEY,
  GET_AD_LAUNCHES_QUERY_KEY,
} from "../../utils/constants/queryKeys";
import {
  agencyDisplayName,
  getAgencyIdFromUser,
  isAgencyScopedUser,
} from "../../utils/agencyScope";
import { formatDisplayDateTime } from "../../utils/helperFunctions";
import { displayIdOrOrphan, isOrphanId } from "../../utils/constants/ads";
import { copyTextToClipboard } from "../../utils/clipboard";
import { cn } from "../../lib/utils";
import { deliveryModerationChipStatus } from "../../utils/deliveryModeration";
import SectionHeader from "../common/SectionHeader";
import StatusChip from "../common/StatusChip";
import CustomModal from "../common/CustomModal";
import RequestedByFilter from "../common/RequestedByFilter";
import DateRangeFilter from "../common/DateRangeFilter";
import PaginationFooter from "../common/PaginationFooter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";

const StageCard = ({ title, total, breakdown, to, color = "#4b5563" }) => (
  <NavLink to={to} className="block flex-1 min-w-0 no-underline">
    <Card
      variant="outlined"
      sx={{
        fontFamily: "'DM Sans', system-ui, sans-serif",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        height: "100%",
        transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
        "&:hover": {
          borderColor: "#7f7ad5",
          boxShadow: "0 4px 12px rgba(127, 122, 213, 0.12)",
          backgroundColor: "rgba(127, 122, 213, 0.02)",
        },
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "inherit",
            color: "#6b7280",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontSize: "0.7rem",
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: "inherit",
            fontWeight: 700,
            mt: 0.25,
            color,
            fontSize: "1.5rem",
          }}
        >
          {total}
        </Typography>
        {breakdown && breakdown.length > 0 && (
          <Box
            component="ul"
            sx={{
              mt: 1,
              pl: 0,
              m: 0,
              listStyle: "none",
              display: "flex",
              flexWrap: "wrap",
              columnGap: 1.5,
              rowGap: 0.25,
            }}
          >
            {breakdown.map(([label, value]) => (
              <Typography
                key={label}
                component="li"
                variant="body2"
                sx={{
                  fontFamily: "inherit",
                  color: "#6b7280",
                  fontSize: "0.8125rem",
                }}
              >
                {label}:{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: "#374151" }}
                >
                  {value}
                </Box>
              </Typography>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  </NavLink>
);

const Arrow = () => (
  <Box
    sx={{
      flex: "0 0 auto",
      display: "flex",
      alignItems: "center",
      px: 1.5,
      color: "#9ca3af",
    }}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  </Box>
);

const JOURNEY_LIMIT = 20;

// Journey status filter options — full pipeline_status set with labels matching
// the Status chips (see STATUS_CONFIG in common/StatusChip.jsx).
const JOURNEY_STATUS_OPTIONS = [
  { value: "delivery_pending", label: "Delivery pending" },
  { value: "qc_pending", label: "QC pending" },
  { value: "qc_changes_requested", label: "QC changes requested" },
  { value: "moderation_pending", label: "Moderation pending" },
  { value: "moderation_approved", label: "Approved" },
  { value: "moderation_rejected", label: "Rejected" },
  { value: "launch_pending", label: "Launch pending" },
  { value: "launch_queued", label: "Queued" },
  { value: "launch_processing", label: "Launching" },
  { value: "launch_failed", label: "Launch failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "live", label: "Live" },
  { value: "switched_off", label: "Switch off" },
];

// Paise (integer) → "₹1,234". Returns "—" for null/undefined so empty cells read cleanly.
const fmtMoneyPaise = (paise) => {
  if (paise == null) return "—";
  return `₹${Math.round(paise / 100).toLocaleString("en-IN")}`;
};

// Resolve the exact moderation chip variant used by the Ad Moderation view
// (final_approved / final_rejected / moderation_*), from the raw journey fields.
const moderationChipKey = (r) =>
  deliveryModerationChipStatus({
    finalized_status: r?.finalized_status,
    moderation_status: r?.moderation_decision,
  });

const jThSx = {
  py: 1.75,
  px: 2,
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "#4b5563",
  border: "none",
  whiteSpace: "nowrap",
};
const jTableSx = {
  fontFamily: "'DM Sans', system-ui, sans-serif",
  "& .MuiTableCell-root": { fontFamily: "'DM Sans', system-ui, sans-serif" },
  "& .MuiTableBody-root .MuiTableCell-root": {
    borderBottom: "1px solid #f3f4f6",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    borderLeft: "none",
    borderRight: "none",
    borderTop: "none",
    backgroundColor: "#fafafa",
  },
};

// One clickable internal ID → its source view. Clicking navigates to that view
// pre-filtered to this record (backend search matches numeric pk/ad_id/etc.).
const IdLink = ({ label, value, to, onNavigate }) => {
  if (value == null) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm text-gray-400">—</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onNavigate(to, value)}
      className="w-full flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0 group text-left"
    >
      <span className="text-sm text-gray-500 group-hover:text-gray-700">
        {label}
      </span>
      <span className="text-sm font-medium text-[#4f46e5] group-hover:underline">
        #{value}
      </span>
    </button>
  );
};

// Read-only Meta identifier row (no internal view to deep-link to).
const MetaIdRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-mono text-gray-700 break-all text-right">
      {value || "—"}
    </span>
  </div>
);

// "Show all the IDs for this ad and jump to the respective view." Opened by
// clicking a journey row; each internal ID routes to its source screen.
const JourneyDetailsModal = ({ row, onClose }) => {
  const navigate = useNavigate();
  if (!row) return null;

  const goTo = (to, value) => {
    onClose();
    navigate(`${to}?query=${encodeURIComponent(value)}`);
  };

  const hasMeta =
    row.meta_ad_id ||
    row.meta_adset_id ||
    row.meta_campaign_id ||
    row.meta_creative_id;

  return (
    <CustomModal
      open={!!row}
      onClose={onClose}
      title={row.ad_name || `Ad #${row.ad_id ?? "—"}`}
      isButtonsRequired={false}
      className="md:w-[55%] lg:w-[40%]"
    >
      <div className="space-y-4 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {moderationChipKey(row) && (
            <StatusChip subtle status={moderationChipKey(row)} />
          )}
          {row.current_status && (
            <StatusChip subtle status={row.current_status} />
          )}
          {row.track && (
            <span className="text-xs text-gray-500">Track: {row.track}</span>
          )}
          {row.for_app_name && (
            <span className="text-xs text-gray-500">
              · App: {row.for_app_name}
            </span>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
            Journey IDs
          </p>
          <p className="text-xs text-gray-400 mb-1">
            Click an ID to open it in its view
          </p>
          <div className="rounded-lg border border-gray-200 px-3">
            {/* Ad-request search matches parent ad id (not request id), so navigate
                with ad_id; the displayed value stays the request id. */}
            <IdLink
              label="Request ID"
              value={row.ad_request_id}
              to="/dashboard/ad-request"
              onNavigate={(to) =>
                goTo(to, isOrphanId(row.ad_id) ? row.ad_request_id : row.ad_id)
              }
            />
            <IdLink
              label="Deliver ID"
              value={row.ad_deliver_id}
              to="/dashboard/ad-upload"
              onNavigate={goTo}
            />
            <IdLink
              label="Moderation ID"
              value={row.ad_moderate_id}
              to="/dashboard/ad-moderation"
              onNavigate={goTo}
            />
            <IdLink
              label="Parent Ad ID"
              value={isOrphanId(row.ad_id) ? null : row.ad_id}
              to="/dashboard/ad-moderation"
              onNavigate={goTo}
            />
            <IdLink
              label="Launch ID"
              value={row.launch_id}
              to="/dashboard/ad-launch"
              onNavigate={goTo}
            />
          </div>
        </div>

        {hasMeta && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
              Meta IDs
            </p>
            <div className="rounded-lg border border-gray-200 px-3">
              <MetaIdRow label="Meta Ad ID" value={row.meta_ad_id} />
              <MetaIdRow label="Meta Adset ID" value={row.meta_adset_id} />
              <MetaIdRow
                label="Meta Campaign ID"
                value={row.meta_campaign_id}
              />
              <MetaIdRow
                label="Meta Creative ID"
                value={row.meta_creative_id}
              />
            </div>
            {row.meta_adset_url && (
              <a
                href={row.meta_adset_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-sm text-[#4f46e5] hover:underline"
              >
                Open in Meta Ads Manager →
              </a>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
};

// Consolidated leadership view of every ad's full journey: request → deliver →
// moderate → launch, plus live Meta status/spend/conversions from AdInsights.
const JourneyTable = ({
  axiosPrivate,
  dateFrom,
  dateTo,
  deliveredFrom,
  deliveredTo,
  requestedBy,
  journeyStatus,
  canRefresh,
  canSetLive,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(JOURNEY_LIMIT);
  const [expandedHookId, setExpandedHookId] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const queryClient = useQueryClient();

  // Explicit ranges: dateFrom/dateTo is the dashboard's "Requested Date" filter,
  // deliveredFrom/deliveredTo its "Delivered Date" filter. (Legacy date_from/date_to
  // on /launch/ means delivered-date for pipeline rows, so it is NOT used here.)
  const params = {
    axiosPrivate,
    requested_date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined,
    requested_date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : undefined,
    delivered_date_from: deliveredFrom
      ? format(deliveredFrom, "yyyy-MM-dd")
      : undefined,
    delivered_date_to: deliveredTo
      ? format(deliveredTo, "yyyy-MM-dd")
      : undefined,
    requested_by_id: requestedBy ? Number(requestedBy) : undefined,
    journey_status: journeyStatus || undefined,
    limit: pageSize,
    offset: page * pageSize,
  };

  const { data: result, isLoading } = useQuery({
    queryKey: [
      ...GET_AD_LAUNCHES_QUERY_KEY,
      "journey",
      dateFrom,
      dateTo,
      deliveredFrom,
      deliveredTo,
      requestedBy,
      journeyStatus,
      page,
      pageSize,
    ],
    queryFn: () => AdsAPIs.listLaunches(params),
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => result?.data ?? [], [result]);
  const totalCount = result?.count ?? 0;
  const lastSyncedAt = result?.last_synced_at ?? null;

  useEffect(() => {
    setPage(0);
  }, [
    dateFrom,
    dateTo,
    deliveredFrom,
    deliveredTo,
    requestedBy,
    journeyStatus,
  ]);

  const refreshMutation = useMutation({
    mutationFn: (launchId) =>
      AdsAPIs.refreshLaunchStatus({ axiosPrivate, id: launchId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_LAUNCHES_QUERY_KEY });
      toast.success("Status refreshed from Meta.");
    },
  });

  const setLiveMutation = useMutation({
    mutationFn: (adModerateId) =>
      AdsAPIs.markLaunchLive({ axiosPrivate, ad_moderate_id: adModerateId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_LAUNCHES_QUERY_KEY });
      toast.success("Marked as Live.");
    },
  });

  const colSpan = 11;

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden mt-6">
      <div className="px-4 py-3 border-b border-gray-200">
        <Typography
          sx={{
            fontFamily: "inherit",
            fontWeight: 600,
            color: "#374151",
            fontSize: "0.95rem",
          }}
        >
          Ad Journey
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mt: 0.25,
          }}
        >
          <Typography
            sx={{
              fontFamily: "inherit",
              color: "#6b7280",
              fontSize: "0.8125rem",
            }}
          >
            Full lifecycle per ad — request to live performance on Meta
          </Typography>
          {lastSyncedAt && (
            <Typography
              sx={{
                fontFamily: "inherit",
                color: "#9ca3af",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
              }}
            >
              Last auto-refreshed at {formatDisplayDateTime(lastSyncedAt)}
            </Typography>
          )}
        </Box>
      </div>
      <TableContainer>
        <Table size="medium" sx={jTableSx}>
          <TableHead>
            <TableRow sx={{ borderBottom: "1px solid #e5e7eb" }}>
              <TableCell sx={jThSx}>Parent Ad ID</TableCell>
              <TableCell sx={jThSx}>Hook Name</TableCell>
              <TableCell sx={jThSx}>Launch ID</TableCell>
              <TableCell sx={jThSx}>Request Date</TableCell>
              <TableCell sx={jThSx}>Delivered Date</TableCell>
              <TableCell sx={jThSx}>Status</TableCell>
              <TableCell sx={jThSx}>Live Date</TableCell>
              <TableCell sx={jThSx}>Total Spend</TableCell>
              <TableCell sx={jThSx}>Cost / Payment</TableCell>
              <TableCell sx={jThSx}>D0 paid user</TableCell>
              {(canRefresh || canSetLive) && (
                <TableCell sx={jThSx} align="right">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                  >
                    <CircularProgress size={22} sx={{ color: "#7f7ad5" }} />
                    <Typography
                      component="span"
                      fontWeight={500}
                      sx={{ color: "#7f7ad5" }}
                    >
                      Loading…
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((r) => {
                const isLaunched =
                  (r.status || "").toLowerCase() === "success" && r.launch_id;
                const rowKey = `${r.ad_moderate_id}-${r.launch_id || 0}`;
                const hookText =
                  r.hook_name || r.ad_name || `Ad ${r.hook_number ?? 1}`;
                return (
                  <TableRow
                    key={`${r.ad_moderate_id}-${r.launch_id || 0}`}
                    hover
                    onClick={() => setDetailRow(r)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ color: "text.secondary" }}>
                      {displayIdOrOrphan(r.ad_id)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", maxWidth: 220 }}>
                      <div className="flex items-center gap-1 max-w-[200px]">
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyTextToClipboard(hookText);
                            }}
                            aria-label="Copy hook name"
                            sx={{ p: 0.25 }}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </IconButton>
                        </Tooltip>
                        <span
                          className={cn(
                            "select-text text-sm cursor-pointer hover:underline",
                            expandedHookId === rowKey
                              ? "break-all"
                              : "truncate block max-w-[160px]"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            const sel =
                              (typeof window !== "undefined" &&
                                window.getSelection &&
                                window.getSelection().toString()) ||
                              "";
                            if (sel.length > 0) return;
                            setExpandedHookId((prev) =>
                              prev === rowKey ? null : rowKey
                            );
                          }}
                          title="Click to expand"
                        >
                          {hookText}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {r.launch_id ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.request_date
                        ? formatDisplayDateTime(r.request_date)
                        : "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.delivered_date
                        ? formatDisplayDateTime(r.delivered_date)
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {r.pipeline_status ? (
                        <StatusChip subtle status={r.pipeline_status} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.live_date ? formatDisplayDateTime(r.live_date) : "—"}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {fmtMoneyPaise(r.total_spend)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {fmtMoneyPaise(r.cost_per_payment)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {r.d0_paid_users ?? "—"}
                    </TableCell>
                    {(canRefresh || canSetLive) && (
                      <TableCell
                        align="right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1.5">
                          {canRefresh && isLaunched && (
                            <Tooltip
                              title="Refresh live status & spend from Meta"
                              arrow
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  refreshMutation.mutate(r.launch_id)
                                }
                                disabled={refreshMutation.isPending}
                                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Refresh
                              </button>
                            </Tooltip>
                          )}
                          {canSetLive &&
                            r.finalized_status === "approved" &&
                            r.current_status !== "live" && (
                              <Tooltip
                                title="Mark as Live (for ads launched outside the platform)"
                                arrow
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setLiveMutation.mutate(r.ad_moderate_id)
                                  }
                                  disabled={setLiveMutation.isPending}
                                  className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium text-emerald-700 bg-white border border-emerald-200 shadow-sm hover:bg-emerald-50 transition-colors disabled:opacity-50"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  Mark Live
                                </button>
                              </Tooltip>
                            )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  No ads found for the selected filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationFooter
        shown={rows.length}
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(0);
        }}
      />

      <JourneyDetailsModal row={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  );
};

const AdsSection = () => {
  const axiosPrivate = useAxiosPrivate();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const { hasNavLinkAccess, hasPermission } = usePermissions();
  const canListAgencies = AGENCY_LIST_PERMISSIONS.some((p) => hasPermission(p));
  const scopedAgencyId = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  // Filters are seeded from the URL query so they persist across reload and are shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(() => {
    const v = searchParams.get("date_from");
    return v ? parseISO(v) : null;
  });
  const [dateTo, setDateTo] = useState(() => {
    const v = searchParams.get("date_to");
    return v ? parseISO(v) : null;
  });
  const [agencyFilter, setAgencyFilter] = useState(() => {
    if (scopedAgencyId != null) return String(scopedAgencyId);
    return searchParams.get("agency") || "";
  });
  const [requestedByFilter, setRequestedByFilter] = useState(
    () => searchParams.get("requested_by") || ""
  );
  const [deliveredFrom, setDeliveredFrom] = useState(() => {
    const v = searchParams.get("delivered_from");
    return v ? parseISO(v) : null;
  });
  const [deliveredTo, setDeliveredTo] = useState(() => {
    const v = searchParams.get("delivered_to");
    return v ? parseISO(v) : null;
  });
  const [journeyStatusFilter, setJourneyStatusFilter] = useState(
    () => searchParams.get("journey_status") || ""
  );

  // Mirror the active filters back into the URL (replace, so no history spam).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const setOrDelete = (key, val) =>
          val ? next.set(key, val) : next.delete(key);
        setOrDelete(
          "date_from",
          dateFrom ? format(dateFrom, "yyyy-MM-dd") : ""
        );
        setOrDelete("date_to", dateTo ? format(dateTo, "yyyy-MM-dd") : "");
        setOrDelete(
          "delivered_from",
          deliveredFrom ? format(deliveredFrom, "yyyy-MM-dd") : ""
        );
        setOrDelete(
          "delivered_to",
          deliveredTo ? format(deliveredTo, "yyyy-MM-dd") : ""
        );
        if (!agencyScoped) setOrDelete("agency", agencyFilter);
        setOrDelete("requested_by", requestedByFilter);
        setOrDelete("journey_status", journeyStatusFilter);
        return next;
      },
      { replace: true }
    );
  }, [
    dateFrom,
    dateTo,
    deliveredFrom,
    deliveredTo,
    agencyFilter,
    requestedByFilter,
    journeyStatusFilter,
    agencyScoped,
    setSearchParams,
  ]);
  const showAdLaunchCard = hasNavLinkAccess("/dashboard/ad-launch");
  const canRefreshStatus = hasPermission(PERMISSION_CODES.AD_LAUNCH_CREATE);
  const canSetLive = hasPermission(PERMISSION_CODES.AD_LAUNCH_SET_LIVE);
  const showRequestCard = hasNavLinkAccess("/dashboard/ad-request");
  const showDeliverCard = hasNavLinkAccess("/dashboard/ad-upload");
  // QC lives inside the ad-request flow; the card deep-links to requests
  // filtered to deliveries awaiting QC.
  const showQcCard = hasPermission(PERMISSION_CODES.AD_QC_REVIEW);
  const showModerationCard = hasNavLinkAccess("/dashboard/ad-moderation");

  const { data: agenciesResult } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () =>
      AgencyAPIs.list({
        axiosPrivate,
        status: "approved",
        limit: 200,
        offset: 0,
      }),
    enabled: !agencyScoped && canListAgencies,
  });
  const agencies = agenciesResult?.data ?? [];

  const requesterFilterAgencyId =
    scopedAgencyId ?? (agencyFilter ? Number(agencyFilter) : undefined);

  const { data: journey, isLoading } = useQuery({
    queryKey: [
      ...GET_AD_JOURNEY_QUERY_KEY,
      dateFrom,
      dateTo,
      deliveredFrom,
      deliveredTo,
      agencyFilter,
      requestedByFilter,
    ],
    queryFn: () =>
      AdsAPIs.getAdJourneyStats({
        axiosPrivate,
        date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined,
        date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : undefined,
        delivered_date_from: deliveredFrom
          ? format(deliveredFrom, "yyyy-MM-dd")
          : undefined,
        delivered_date_to: deliveredTo
          ? format(deliveredTo, "yyyy-MM-dd")
          : undefined,
        requested_for_agency_id:
          agencyScoped && scopedAgencyId != null
            ? scopedAgencyId
            : agencyFilter
              ? Number(agencyFilter)
              : undefined,
        requested_by_id: requestedByFilter
          ? Number(requestedByFilter)
          : undefined,
      }),
    // Keep the previous stats on screen while a filter change refetches — the
    // full-page spinner (and the JourneyTable unmount it caused) only happens
    // on the very first load.
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return (
      <section className="p-4 md:p-6">
        <SectionHeader title="Ads" className="mb-4" />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={280}
          gap={2}
        >
          <CircularProgress size={28} sx={{ color: "#7f7ad5" }} />
          <Typography
            component="span"
            fontWeight={500}
            sx={{ color: "#7f7ad5" }}
          >
            Loading…
          </Typography>
        </Box>
      </section>
    );
  }

  const req = journey?.ad_requests ?? {};
  const deliv = journey?.ad_deliveries ?? {};
  const qc = journey?.ad_qc ?? {};
  const mod = journey?.ad_moderations ?? {};
  const launch = journey?.ad_launches ?? {};

  return (
    <section
      className="p-4 md:p-6"
      style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <SectionHeader title="Ads" className="mb-4" />
      <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-4">
            <DateRangeFilter
              label="Requested Date"
              from={dateFrom}
              to={dateTo}
              onChange={(f, t) => {
                setDateFrom(f);
                setDateTo(t);
              }}
            />
            <DateRangeFilter
              label="Delivered Date"
              from={deliveredFrom}
              to={deliveredTo}
              onChange={(f, t) => {
                setDeliveredFrom(f);
                setDeliveredTo(t);
              }}
            />
            <Select
              value={journeyStatusFilter || "all"}
              onValueChange={(v) =>
                setJourneyStatusFilter(v === "all" ? "" : v)
              }
            >
              <SelectTrigger className="h-9 w-[180px] bg-gray-50/80 border-gray-200 hover:bg-white transition-colors">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {JOURNEY_STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!agencyScoped && canListAgencies && (
              <Select
                value={agencyFilter || "all"}
                onValueChange={(v) => setAgencyFilter(v === "all" ? "" : v)}
              >
                <SelectTrigger className="h-9 w-[200px] bg-gray-50/80 border-gray-200 hover:bg-white transition-colors">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {agencies.map((a, i) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {agencyDisplayName(a, i)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <RequestedByFilter
              value={requestedByFilter}
              onChange={setRequestedByFilter}
              agencyId={requesterFilterAgencyId}
            />
            {(dateFrom ||
              dateTo ||
              deliveredFrom ||
              deliveredTo ||
              journeyStatusFilter ||
              (!agencyScoped && agencyFilter) ||
              requestedByFilter) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setDateFrom(null);
                  setDateTo(null);
                  setDeliveredFrom(null);
                  setDeliveredTo(null);
                  setJourneyStatusFilter("");
                  if (!agencyScoped) setAgencyFilter("");
                  setRequestedByFilter("");
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>
        <div className="px-4 py-3">
          <p className="text-sm text-gray-500 mb-2 font-medium">
            Count of items in each stage of the ad journey
          </p>
          <Box display="flex" flexWrap="wrap" alignItems="stretch" gap={2}>
            {showRequestCard && (
              <>
                <StageCard
                  title="Ad Requested"
                  total={req.total ?? 0}
                  breakdown={[
                    ["Pending", req.pending ?? 0],
                    ["Delivered", req.delivered ?? 0],
                    ["Delayed", req.delayed ?? req.rejected ?? 0],
                    ["Cancelled", req.cancelled ?? 0],
                  ]}
                  to="/dashboard/ad-request"
                  color="#7f7ad5"
                />
                {(showDeliverCard ||
                  showModerationCard ||
                  showAdLaunchCard) && <Arrow />}
              </>
            )}
            {showDeliverCard && (
              <>
                <StageCard
                  title="Ad Delivered"
                  total={deliv.total ?? 0}
                  to="/dashboard/ad-upload"
                  color="#059669"
                />
                {(showQcCard || showModerationCard || showAdLaunchCard) && (
                  <Arrow />
                )}
              </>
            )}
            {showQcCard && (
              <>
                <StageCard
                  title="Delivery QC"
                  total={(qc.pending ?? 0) + (qc.changes_requested ?? 0)}
                  breakdown={[
                    ["Pending", qc.pending ?? 0],
                    ["Changes req.", qc.changes_requested ?? 0],
                    ["Approved", qc.approved ?? 0],
                  ]}
                  to="/dashboard/ad-request?qc_status=pending"
                  color="#d97706"
                />
                {(showModerationCard || showAdLaunchCard) && <Arrow />}
              </>
            )}
            {showModerationCard && (
              <>
                <StageCard
                  title="Ad Moderated"
                  total={mod.total ?? 0}
                  breakdown={[
                    ["Pending", mod.pending ?? 0],
                    ["Approved", mod.approved ?? 0],
                    ["Rejected", mod.rejected ?? 0],
                  ]}
                  to="/dashboard/ad-moderation"
                  color="#b45309"
                />
                {showAdLaunchCard && <Arrow />}
              </>
            )}
            {showAdLaunchCard && (
              <StageCard
                title="Ad Launched"
                total={launch.total ?? 0}
                breakdown={[
                  ["Ready", launch.ready ?? 0],
                  ["Queued", launch.queued ?? 0],
                  ["Processing", launch.processing ?? 0],
                  ["Success", launch.success ?? 0],
                  ["Failed", launch.failed ?? 0],
                ]}
                to="/dashboard/ad-launch"
                color="#0d9488"
              />
            )}
          </Box>
        </div>
      </div>

      {showAdLaunchCard && (
        <JourneyTable
          axiosPrivate={axiosPrivate}
          dateFrom={dateFrom}
          dateTo={dateTo}
          deliveredFrom={deliveredFrom}
          deliveredTo={deliveredTo}
          requestedBy={requestedByFilter}
          journeyStatus={journeyStatusFilter}
          canRefresh={canRefreshStatus}
          canSetLive={canSetLive}
        />
      )}
    </section>
  );
};

export default AdsSection;
